import { Event } from 'services/events/types';
import { formatShortDueDate } from 'utils/date';
import { DURATION_FORMAT, formatObjectiveQuantity } from '../formatUnit';

const IGNORED_OBJECTIVE_DIFF_KEYS = new Set([
  '__v',
  '_id',
  'archivedAt',
  'cancelledAt',
  'completedAt',
  'createdAt',
  'isCancelled',
  'id',
  'modifiedAt',
  'unitPosition',
  'userId',
]);

const OBJECTIVE_FIELD_LABEL_KEYS: Record<string, string> = {
  assignee: 'assignee',
  cancelReason: 'cancelReason',
  description: 'description',
  dueDate: 'deadline',
  goal: 'goal',
  format: 'durationFormat',
  goalFrequency: 'goalFrequency',
  goalKind: 'goalKind',
  goalTolerance: 'goalTolerance',
  initialProgress: 'initialProgress',
  targets: 'targets',
  isArchived: 'archive',
  isCompleted: 'completed',
  name: 'name',
  progress: 'progress',
  projects: 'projects',
  status: 'status',
  unit: 'unit',
};

const OBJECTIVE_HISTORY_FIELD_ORDER = [
  'name',
  'description',
  'status',
  'assignee',
  'cancelReason',
  'progress',
  'goal',
  'goalKind',
  'goalTolerance',
  'goalFrequency',
  'format',
  'unit',
  'targets',
  'dueDate',
  'initialProgress',
  'projects',
  'isCompleted',
  'isArchived',
];

type Translate = (key: string, options?: Record<string, unknown>) => string;

type SnapshotField = 'assignee' | 'dueDate' | 'format' | 'goalFrequency' | 'goalKind' | 'isCompleted' | 'progress' | 'status' | 'unit';

interface ProjectRef {
  id?: string;
  name?: string;
}

interface SummarizeObjectiveEventOptions {
  projectName?: (projectId: string) => string | undefined;
  userName?: (userId: string) => string | undefined;
}

const collectKeys = (obj: Record<string, unknown> | undefined, keys: Set<string>) => {
  if (!obj) {
    return;
  }
  Object.keys(obj).forEach(key => {
    if (!IGNORED_OBJECTIVE_DIFF_KEYS.has(key)) {
      keys.add(key);
    }
  });
};

const getChangedObjectiveFieldKeys = (event: Event): string[] => {
  const keys = new Set<string>();
  collectKeys(event.diff, keys);
  collectKeys(event.detailedDiff?.added, keys);
  collectKeys(event.detailedDiff?.deleted, keys);
  collectKeys(event.detailedDiff?.updated, keys);

  return [...keys].sort((a, b) => {
    const aIndex = OBJECTIVE_HISTORY_FIELD_ORDER.indexOf(a);
    const bIndex = OBJECTIVE_HISTORY_FIELD_ORDER.indexOf(b);
    const aOrder = aIndex === -1 ? Number.MAX_SAFE_INTEGER : aIndex;
    const bOrder = bIndex === -1 ? Number.MAX_SAFE_INTEGER : bIndex;
    if (aOrder !== bOrder) {
      return aOrder - bOrder;
    }
    return a.localeCompare(b);
  });
};

const joinWithAnd = (items: string[], andWord: string): string => {
  if (items.length === 0) {
    return '';
  }
  if (items.length === 1) {
    return items[0];
  }
  if (items.length === 2) {
    return `${items[0]} ${andWord} ${items[1]}`;
  }
  return `${items.slice(0, -1).join(', ')} ${andWord} ${items[items.length - 1]}`;
};

const readSnapshotField = (event: Event, field: SnapshotField): unknown => {
  const current = event.objective;
  const previous = event.oldObjective;
  if (current && field in current) {
    return current[field];
  }
  if (event.diff && field in event.diff) {
    return event.diff[field];
  }
  if (event.detailedDiff?.updated && field in event.detailedDiff.updated) {
    return event.detailedDiff.updated[field];
  }
  if (event.detailedDiff?.added && field in event.detailedDiff.added) {
    return event.detailedDiff.added[field];
  }
  if (current && previous && field in previous && !(field in current)) {
    return null;
  }
  if (event.detailedDiff?.deleted && field in event.detailedDiff.deleted) {
    return null;
  }
  return undefined;
};

const describeAssignee = (
  event: Event,
  t: Translate,
  userName?: (userId: string) => string | undefined,
): string | null => {
  const value = readSnapshotField(event, 'assignee');
  if (value === undefined) {
    return null;
  }
  if (value === null || value === '') {
    return t('objectiveHistoryAssigneeCleared');
  }
  if (typeof value !== 'string') {
    return null;
  }
  return t('objectiveHistoryAssigneeSet', {
    assignee: userName?.(value) || t('unknownUser'),
  });
};

const describeDeadline = (event: Event, t: Translate): string | null => {
  const value = readSnapshotField(event, 'dueDate');
  if (value === undefined) {
    return null;
  }
  if (value === null || value === '') {
    return t('objectiveHistoryDeadlineCleared');
  }
  const date = value instanceof Date ? value : new Date(String(value));
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return t('objectiveHistoryDeadlineSet', {
    date: formatShortDueDate(date),
  });
};

const describeProgress = (event: Event, t: Translate): string | null => {
  const value = readSnapshotField(event, 'progress');
  if (value === undefined) {
    return null;
  }
  if (value === null || value === '') {
    return t('objectiveHistoryProgressCleared');
  }
  const progress = Number(value);
  if (Number.isNaN(progress)) {
    return null;
  }
  const snapshot = event.objective;
  return t('objectiveHistoryProgressSet', {
    value: formatObjectiveQuantity(progress, snapshot && {
      format: snapshot.format === DURATION_FORMAT ? DURATION_FORMAT : null,
      unit: snapshot.unit,
    }),
  });
};

const describeFormat = (event: Event, t: Translate): string | null => {
  const value = readSnapshotField(event, 'format');
  if (value === undefined) {
    return null;
  }
  if (value === 'duration') {
    return t('objectiveHistoryFormatSet', { format: t('durationFormat') });
  }
  if (value === null || value === '' || value === 'number') {
    return t('objectiveHistoryFormatCleared');
  }
  return null;
};

const describeUnit = (event: Event, t: Translate): string | null => {
  const value = readSnapshotField(event, 'unit');
  if (value === undefined) {
    return null;
  }
  if (value === null || value === '') {
    return t('objectiveHistoryUnitCleared');
  }
  if (typeof value !== 'string') {
    return null;
  }
  return t('objectiveHistoryUnitSet', { unit: value.trim() });
};

const GOAL_KIND_LABELS: Record<string, string> = {
  AROUND: 'goalKindAround',
  HIGHEST: 'goalKindHighest',
  LOWEST: 'goalKindLowest',
  REACH: 'goalKindReach',
};

const describeGoalKind = (event: Event, t: Translate): string | null => {
  const value = readSnapshotField(event, 'goalKind');
  if (value === undefined) {
    return null;
  }
  if (value === null || value === '') {
    return t('objectiveHistoryGoalKindCleared');
  }
  const labelKey = GOAL_KIND_LABELS[String(value)];
  if (!labelKey) {
    return null;
  }
  return t('objectiveHistoryGoalKindSet', { kind: t(labelKey) });
};

const describeGoalFrequency = (event: Event, t: Translate): string | null => {
  const value = readSnapshotField(event, 'goalFrequency');
  if (value === undefined) {
    return null;
  }
  if (value === null || value === '') {
    return t('objectiveHistoryGoalFrequencyCleared');
  }
  if (value === 'DAILY' || value === 'WEEKLY') {
    return t('objectiveHistoryGoalFrequencySet', {
      frequency: t(value === 'DAILY' ? 'daily' : 'weekly'),
    });
  }
  return null;
};

const STATUS_LABELS: Record<string, string> = {
  Cancelled: 'cancelled',
  Completed: 'completed',
  Doing: 'doing',
  Failed: 'failed',
  Todo: 'todo',
};

const describeStatus = (event: Event, t: Translate): string | null => {
  const value = readSnapshotField(event, 'status');
  const labelKey = typeof value === 'string' ? STATUS_LABELS[value] : undefined;
  if (!labelKey) {
    return null;
  }
  return t('objectiveHistoryStatusSet', { status: t(labelKey) });
};

const describeCompleted = (event: Event, t: Translate): string | null => {
  const value = readSnapshotField(event, 'isCompleted');
  if (typeof value !== 'boolean') {
    return null;
  }
  return value ? t('objectiveHistoryCompleted') : t('objectiveHistoryReopened');
};

const projectRefs = (value: unknown): ProjectRef[] => {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.filter((item): item is ProjectRef => Boolean(item) && typeof item === 'object');
};

const projectIds = (projects: ProjectRef[]): string[] => {
  const ids: string[] = [];
  const seen = new Set<string>();
  projects.forEach(project => {
    if (!project.id || seen.has(project.id)) {
      return;
    }
    seen.add(project.id);
    ids.push(project.id);
  });
  return ids;
};

const nameForProject = (
  id: string,
  snapshots: ProjectRef[],
  projectName?: (projectId: string) => string | undefined,
) => snapshots.find(project => project.id === id)?.name || projectName?.(id) || id;

const describeProjectChanges = (
  event: Event,
  t: Translate,
  projectName?: (projectId: string) => string | undefined,
): string[] | null => {
  const andWord = t('and');
  const oldProjects = projectRefs(event.oldObjective?.projects);
  const newProjects = projectRefs(event.objective?.projects);
  const snapshots = [...newProjects, ...oldProjects];

  if (event.oldObjective || event.objective) {
    if (!event.oldObjective || !event.objective) {
      return null;
    }
    const oldIds = new Set(projectIds(oldProjects));
    const newIds = projectIds(newProjects);
    const assigned = newIds.filter(id => !oldIds.has(id));
    const unassigned = projectIds(oldProjects).filter(id => !newIds.includes(id));
    if (assigned.length === 0 && unassigned.length === 0) {
      return null;
    }
    const phrases: string[] = [];
    if (assigned.length > 0) {
      phrases.push(t('objectiveHistoryProjectsAssigned', {
        count: assigned.length,
        projects: joinWithAnd(assigned.map(id => nameForProject(id, snapshots, projectName)), andWord),
      }));
    }
    if (unassigned.length > 0) {
      phrases.push(t('objectiveHistoryProjectsUnassigned', {
        count: unassigned.length,
        projects: joinWithAnd(unassigned.map(id => nameForProject(id, snapshots, projectName)), andWord),
      }));
    }
    return phrases;
  }

  const added = projectRefs(event.diff?.projects ?? event.detailedDiff?.updated?.projects);
  const addedIds = projectIds(added);
  if (addedIds.length === 0) {
    return null;
  }
  return [t('objectiveHistoryProjectsAssigned', {
    count: addedIds.length,
    projects: joinWithAnd(addedIds.map(id => nameForProject(id, added, projectName)), andWord),
  })];
};

const summarizeObjectiveEvent = (
  event: Event,
  t: Translate,
  options?: SummarizeObjectiveEventOptions,
): string => {
  if (event.eventType === 'CREATED') {
    return t('objectiveCreated');
  }
  if (event.eventType === 'DELETED') {
    return t('objectiveDeleted');
  }

  const fieldKeys = getChangedObjectiveFieldKeys(event);
  if (fieldKeys.length === 0) {
    return t('objectiveUpdated');
  }

  const labels: string[] = [];
  const details: string[] = [];
  fieldKeys.forEach(key => {
    if (key === 'assignee') {
      const assignee = describeAssignee(event, t, options?.userName);
      if (assignee) {
        details.push(assignee);
        return;
      }
    }
    if (key === 'dueDate') {
      const deadline = describeDeadline(event, t);
      if (deadline) {
        details.push(deadline);
        return;
      }
    }
    if (key === 'progress') {
      const progress = describeProgress(event, t);
      if (progress) {
        details.push(progress);
        return;
      }
    }
    if (key === 'goalFrequency') {
      const frequency = describeGoalFrequency(event, t);
      if (frequency) {
        details.push(frequency);
        return;
      }
    }
    if (key === 'goalKind') {
      const goalKind = describeGoalKind(event, t);
      if (goalKind) {
        details.push(goalKind);
        return;
      }
    }
    if (key === 'format') {
      const format = describeFormat(event, t);
      if (format) {
        details.push(format);
        return;
      }
    }
    if (key === 'unit') {
      const unit = describeUnit(event, t);
      if (unit) {
        details.push(unit);
        return;
      }
    }
    if (key === 'projects') {
      const projectChanges = describeProjectChanges(event, t, options?.projectName);
      if (projectChanges) {
        details.push(...projectChanges);
        return;
      }
    }
    if (key === 'status') {
      const status = describeStatus(event, t);
      if (status) {
        details.push(status);
        return;
      }
    }
    if (key === 'isCompleted') {
      if (fieldKeys.includes('status')) {
        return;
      }
      const completed = describeCompleted(event, t);
      if (completed) {
        details.push(completed);
        return;
      }
    }
    labels.push(t(OBJECTIVE_FIELD_LABEL_KEYS[key] ?? key));
  });

  const parts = [
    ...(labels.length > 0
      ? [t('objectiveUpdatedFields', { fields: joinWithAnd(labels, t('and')) })]
      : []),
    ...details,
  ];
  if (parts.length === 0) {
    return t('objectiveUpdated');
  }
  return parts.join('. ');
};

export {
  getChangedObjectiveFieldKeys,
  summarizeObjectiveEvent,
};
